import React from "react";
import {
  Typography as MuiTypography,
  TypographyProps as MuiTypographyProps,
} from "@mui/material";

import { AmeenTypographyVariant } from "../../theme/components/typography.theme";

export type TextVariant = AmeenTypographyVariant;

export interface TextProps extends Omit<MuiTypographyProps, "variant"> {
  variant?: TextVariant;
}

/**
 * Wrapped Typography component — the enforced entry point for the
 * canonical type scale (src/theme/components/typography.theme.ts):
 * Hierarchy (H1–H6), Display, Body, Buttons, Labels.
 *
 * Unlike raw MUI <Typography>, `variant` here only accepts the canonical
 * scale (size/weight/family). Color and layout are not part of a variant:
 *   <Text variant="bodyBase" color="error" sx={{ textAlign: 'right' }}>
 *
 * Use the standard MUI `color` prop for palette-backed colors
 * (primary/secondary/error/warning/info/success/text.primary/
 * text.secondary/text.disabled). For a semantic token with no palette
 * slot, use `sx={{ color: (theme) => theme.ameen.colors... }}`.
 *
 * Forwards its ref to the underlying MUI Typography (and through it, to
 * whatever DOM element actually renders) — same as raw <Typography>, so
 * call sites that need a ref (e.g. scrollHeight/clientHeight overflow
 * measurement) work unchanged after migrating.
 */
export const Text = React.forwardRef<
  React.ElementRef<typeof MuiTypography>,
  TextProps
>(({ variant = "bodyBase", children, ...props }, ref) => {
  return (
    <MuiTypography
      ref={ref}
      variant={variant as MuiTypographyProps["variant"]}
      {...props}
    >
      {children}
    </MuiTypography>
  );
});

Text.displayName = "Text";

export default Text;
